/**
 * Static reward (出局倍数) — principal snapshot + exit multipliers.
 */
import type { TokenId } from './tokens.js';
import { LEDGER_ACTION } from './enums.js';

export type StarPackage = 'E1' | 'E2' | 'E3';
export type ReinvestCoinType = 'sp1' | 'sp2' | 'sp3';

export interface StaticExitRule {
  principal_usd: string;
  exit_multiplier: string;
}

export const MAIN_STAR_PRINCIPAL_USD: Record<StarPackage, string> = {
  E1: '100',
  E2: '1000',
  E3: '10000',
};

/** Binary perf / 对碰累计口径 = 配套奖励值（≠ STATIC_PAYOUT_BASE_USD 静态 gross）。 */
export const BINARY_PERF_REWARD_VALUE_USD = MAIN_STAR_PRINCIPAL_USD;

/** One static payout tranche (register queue_ap / SPx 复投开子) — half of package USD. */
export const STATIC_PAYOUT_BASE_USD: Record<StarPackage, string> = {
  E1: '50',
  E2: '500',
  E3: '5000',
};

export function resolveStaticPayoutBaseUsd(starPackage: StarPackage): string {
  return STATIC_PAYOUT_BASE_USD[starPackage] ?? STATIC_PAYOUT_BASE_USD.E2;
}

export const MAIN_EXIT_MULTIPLIER: Record<string, string> = {
  E1: '3',
  E2: '4',
  E3: '4',
};

export const SUB_REINVEST_EXIT_MULTIPLIER: Record<ReinvestCoinType, string> = {
  sp1: '3',
  sp2: '1.2',
  sp3: '4',
};

/** Ledger actions that count toward static exit cap (excludes daily_settlement). */
export const STATIC_REWARD_ACTIONS = [
  LEDGER_ACTION.STATIC_REWARD_EP1,
  LEDGER_ACTION.STATIC_REWARD_SP1,
  LEDGER_ACTION.STATIC_REWARD_EP2,
  LEDGER_ACTION.STATIC_REWARD_SP2,
  LEDGER_ACTION.STATIC_REWARD_RP,
  LEDGER_ACTION.STATIC_REWARD_FC,
] as const;

/** Static payout legs only — EP1 + SP1 (excludes RP/FC and dynamic EP2/SP2). */
export const STATIC_EP1_SP1_ACTIONS = [
  LEDGER_ACTION.STATIC_REWARD_EP1,
  LEDGER_ACTION.STATIC_REWARD_SP1,
] as const;

export const STATIC_PAYOUT_SPLIT: ReadonlyArray<{
  asset: TokenId;
  pct: string;
  action: (typeof LEDGER_ACTION)[keyof typeof LEDGER_ACTION];
}> = [
  { asset: 'ep1', pct: '0.52', action: LEDGER_ACTION.STATIC_REWARD_EP1 },
  { asset: 'sp1', pct: '0.36', action: LEDGER_ACTION.STATIC_REWARD_SP1 },
  { asset: 'rp', pct: '0.10', action: LEDGER_ACTION.STATIC_REWARD_RP },
  { asset: 'fc', pct: '0.02', action: LEDGER_ACTION.STATIC_REWARD_FC },
];

export const WITHDRAW_SPLIT_STATIC: ReadonlyArray<{
  asset: TokenId;
  pct: string;
  leg: 'credit' | 'fee' | 'burn';
}> = [
  { asset: 'ep1', pct: '0.52', leg: 'credit' },
  { asset: 'sp1', pct: '0.36', leg: 'credit' },
  { asset: 'tp', pct: '0.10', leg: 'fee' },
  { asset: 'fc', pct: '0.02', leg: 'burn' },
];

export const WITHDRAW_SPLIT_DYNAMIC: ReadonlyArray<{
  asset: TokenId;
  pct: string;
  leg: 'credit' | 'fee' | 'burn';
}> = [
  { asset: 'ep2', pct: '0.70', leg: 'credit' },
  { asset: 'sp2', pct: '0.20', leg: 'credit' },
  { asset: 'tp', pct: '0.06', leg: 'fee' },
  { asset: 'fc', pct: '0.04', leg: 'burn' },
];

export function resolveMainExitRule(levelCode: string, starPackage: StarPackage): StaticExitRule {
  const principal = MAIN_STAR_PRINCIPAL_USD[starPackage] ?? MAIN_STAR_PRINCIPAL_USD.E1;
  const mult = MAIN_EXIT_MULTIPLIER[levelCode] ?? MAIN_EXIT_MULTIPLIER.E1;
  return { principal_usd: principal, exit_multiplier: mult };
}

export function resolveSubExitRule(
  starPackage: StarPackage,
  reinvestCoinType: ReinvestCoinType,
): StaticExitRule {
  const principal = MAIN_STAR_PRINCIPAL_USD[starPackage] ?? MAIN_STAR_PRINCIPAL_USD.E1;
  return {
    principal_usd: principal,
    exit_multiplier: SUB_REINVEST_EXIT_MULTIPLIER[reinvestCoinType],
  };
}
